import { createFileRoute, notFound } from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import type { BlogPost } from '@/features/blog/types/blog.types'
import {
  BlogPostBreadcrumb,
  BlogPostContent,
  BlogPostFooter,
  BlogPostHeader,
  TableOfContents,
} from '@/features/blog/components'
import { NotFoundContent } from '@/features/shared/components'
import { generateLinks, generateMetaTags, notFoundSEO } from '@/lib/seo'
import {
  getArticleSchema,
  getBreadcrumbSchema,
  renderJsonLd,
} from '@/lib/structured-data'

/** Strips markdown/MDX syntax down to a plain-text excerpt, for a meta
 * description fallback when a post has no metaDescription or excerpt. */
function getExcerptFromContent(content: string, maxLength = 155): string {
  const plain = content
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[#*_>`~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return plain.length > maxLength
    ? `${plain.slice(0, maxLength).trimEnd()}...`
    : plain
}

// NOTE: keep this as `const x: any = createServerFn(...).handler(...)` — NOT
// `const x = createServerFn(...).handler(...) as any`. TanStack Start's
// compiler only extracts a server function into a server-only chunk when the
// `.handler(...)` call is the *direct, unwrapped* initializer of the const
// (see @tanstack/start-plugin-core's isTopLevelDirectCallCandidate, which
// checks `parent.init === node`). Wrapping the whole expression in
// `as any` makes the parent a TSAsExpression instead of the VariableDeclarator,
// so the compiler silently skips extraction — this file's D1 access
// (including the `cloudflare:workers` import) then ships straight into the
// client bundle and throws in the browser instead of running on the server.
const fetchPostBySlug: any = createServerFn({
  method: 'GET',
}).handler(async (ctx: any) => {
  const { getD1 } =
    await import('@/server/infrastructure/database/d1-connection')
  const { D1BlogPostRepository } =
    await import('@/server/infrastructure/persistence/repositories/D1BlogPostRepository')
  const { GetBlogPostBySlugUseCase } =
    await import('@/server/application/blog/use-cases/GetBlogPostBySlugUseCase')
  const { toPublicBlogPostOutput } =
    await import('@/server/application/blog/dto/BlogPostDto')
  const db = getD1()
  const repository = new D1BlogPostRepository(db)
  const useCase = new GetBlogPostBySlugUseCase(repository)
  const result = await useCase.execute(ctx.data.slug)
  if (!result.success || !result.data || result.data.status !== 'published') {
    return null
  }
  return toPublicBlogPostOutput(result.data as any)
})

export const Route = createFileRoute('/blogs/$slug')({
  loader: (async ({ params }: any) => {
    const post = await fetchPostBySlug({ data: { slug: params.slug } })
    if (!post) throw notFound()
    return post
  }) as any,
  head: ({ loaderData }) => {
    const post = loaderData as BlogPost | null
    if (!post) return { meta: generateMetaTags(notFoundSEO) }

    const description =
      post.metaDescription ||
      post.excerpt ||
      getExcerptFromContent(post.content)

    // Article structured data
    const articleSchema = getArticleSchema({
      title: post.title,
      description,
      slug: post.slug,
      coverImage: post.coverImage,
      publishedAt: post.publishedAt,
      createdAt: post.createdAt,
      updatedAt: post.updatedAt,
      tags: post.tags,
    })

    // Breadcrumb structured data
    const breadcrumbSchema = getBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Blog', url: '/blogs' },
      { name: post.title, url: `/blogs/${post.slug}` },
    ])

    return {
      meta: generateMetaTags({
        title: post.metaTitle || `${post.title} | ModelStack Blog`,
        description,
        canonical: `/blogs/${post.slug}`,
        ogType: 'article',
        ogImage: post.coverImage || '/og-image.png',
        ogImageAlt: post.title,
        keywords: post.tags,
      }),
      links: generateLinks({ canonical: `/blogs/${post.slug}` }),
      scripts: [
        {
          type: 'application/ld+json',
          children: renderJsonLd(articleSchema as any),
        },
        {
          type: 'application/ld+json',
          children: renderJsonLd(breadcrumbSchema as any),
        },
      ],
    }
  },
  notFoundComponent: NotFoundContent,
  component: BlogPostPage,
})

function BlogPostPage() {
  const loaderData = Route.useLoaderData()
  const post = loaderData as BlogPost

  return (
    <div>
      <BlogPostBreadcrumb slug={post.slug} />

      <div className="flex gap-12 xl:gap-16">
        <article className="min-w-0 max-w-3xl flex-1">
          <BlogPostHeader post={post} />
          <BlogPostContent content={post.content} />
          <BlogPostFooter />
        </article>

        {/* Table of contents - hidden below lg */}
        <aside className="hidden w-60 shrink-0 lg:block">
          <TableOfContents content={post.content} />
        </aside>
      </div>
    </div>
  )
}
